import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { exchangeCodeForTokens, storeTokens, getStoredTokens, isTokenExpired } from './authService';

// Max time to wait for auth processing before showing error
const AUTH_PROCESSING_TIMEOUT_MS = 10000;

export interface OAuthCallbackProps {
  /** Path to redirect after successful auth */
  successRedirect: string;
  /** Path to redirect on error (login page) */
  errorRedirect: string;
  /** Callback path for token exchange and URL cleanup */
  callbackPath: string;
  /** Unique prefix for localStorage processing keys (should be unique per callback) */
  processingKeyPrefix: string;
}

/**
 * Shared OAuth callback component that handles the authorization code exchange flow.
 * Used by both admin and user login callbacks.
 */
export function OAuthCallback({
  successRedirect,
  errorRedirect,
  callbackPath,
  processingKeyPrefix,
}: OAuthCallbackProps) {
  const navigate = useNavigate();
  const { setTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessedRef.current) return;

    const handleCallback = async () => {
      // Check if we already have valid tokens (already logged in)
      const existingTokens = getStoredTokens();
      if (existingTokens && !isTokenExpired(existingTokens.accessToken)) {
        await setTokens(existingTokens);
        navigate(successRedirect);
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorParam = params.get('error');

      if (errorParam) {
        setError(`Authentication error: ${errorParam}`);
        return;
      }

      if (!code) {
        // Check if auth is currently being processed (handles HMR/re-renders after URL is cleared)
        const processingKeys = Object.keys(localStorage).filter(key => key.startsWith(processingKeyPrefix));

        if (processingKeys.length > 0) {
          // Auth may be in progress - poll for tokens with timeout
          const startTime = Date.now();
          const pollForTokens = async () => {
            const tokens = getStoredTokens();
            if (tokens && !isTokenExpired(tokens.accessToken)) {
              // Tokens appeared, clean up and navigate
              for (const key of processingKeys) {
                localStorage.removeItem(key);
              }
              await setTokens(tokens);
              navigate(successRedirect);
              return;
            }

            if (Date.now() - startTime < AUTH_PROCESSING_TIMEOUT_MS) {
              // Keep polling
              setTimeout(pollForTokens, 200);
            } else {
              // Timeout - clean up stale keys and show error
              for (const key of processingKeys) {
                localStorage.removeItem(key);
              }
              setError('Authentication timed out. Please try again.');
            }
          };
          pollForTokens();
          return;
        }

        // No code and no processing in progress
        const tokens = getStoredTokens();
        if (tokens && !isTokenExpired(tokens.accessToken)) {
          await setTokens(tokens);
          navigate(successRedirect);
          return;
        }
        setError('No authorization code received');
        return;
      }

      // Mark as processed to prevent double execution
      hasProcessedRef.current = true;

      // Use localStorage to prevent double execution (survives HMR)
      const processingKey = `${processingKeyPrefix}${code.substring(0, 20)}`;
      if (localStorage.getItem(processingKey)) {
        // Already processing this code, wait for result
        return;
      }

      try {
        localStorage.setItem(processingKey, 'true');

        // Clear code from URL immediately
        window.history.replaceState({}, '', callbackPath);

        const tokens = await exchangeCodeForTokens(code, callbackPath);
        storeTokens(tokens);
        await setTokens(tokens);
        localStorage.removeItem(processingKey);

        // Navigate to success destination
        navigate(successRedirect);
      } catch (err) {
        console.error('Failed to exchange code for tokens:', err);
        localStorage.removeItem(processingKey);

        // If exchange failed but we already have valid tokens (race condition),
        // just navigate to success destination
        const existingTokens = getStoredTokens();
        if (existingTokens && !isTokenExpired(existingTokens.accessToken)) {
          await setTokens(existingTokens);
          navigate(successRedirect);
          return;
        }

        setError('Failed to complete authentication. Please try again.');
      }
    };

    handleCallback();
  }, [navigate, setTokens, successRedirect, callbackPath, processingKeyPrefix]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="max-w-md w-full p-8 rounded-2xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 backdrop-blur-sm">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Authentication Error</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button
            type="button"
            onClick={() => navigate(errorRedirect)}
            className="w-full px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-500 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <div className="max-w-md w-full p-8 rounded-2xl border border-gray-800 bg-gradient-to-b from-gray-800/50 to-gray-900/50 backdrop-blur-sm text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Authenticating...</h2>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto" />
        <p className="mt-4 text-gray-400">Please wait while we complete your sign-in</p>
      </div>
    </div>
  );
}
